/* Generic, lightweight 2-up mobile carousel controller
   - No clones
   - Works tablet-down only
   - Arrow click + swipe
   - Toggle via data-mc-enabled
*/
(() => {
  const MOBILE_MAX = 989;

  function initCarousel(root) {
    if (!root) return;

    const isMobile = () => window.matchMedia(`(max-width:${MOBILE_MAX}px)`).matches;
    const enabled = () => root.getAttribute('data-mc-enabled') === 'true';

    const getTrack = () => root.querySelector('[data-mc-track]');
    const getPrev  = () => root.querySelector('[data-mc-prev]');
    const getNext  = () => root.querySelector('[data-mc-next]');
    const getItems = () => root.querySelectorAll('[data-mc-track] .grid__item');

    let trackEl = null;
    let index = 0;
    let step = 0;
    let rafScroll = null;

    function computeStep(track) {
      const first = root.querySelector('[data-mc-track] .grid__item');
      if (!track || !first) return 0;
      const rect = first.getBoundingClientRect();
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const w = rect.width > 0 ? rect.width : first.offsetWidth;
      return w + gap;
    }

    function maxStartIdx() {
      const total = getItems().length;
      return Math.max(0, total - 2); // 2 visible at once
    }

    function snap(i, smooth = true) {
      const track = trackEl || getTrack();
      if (!track) return;
      index = Math.min(Math.max(i, 0), maxStartIdx());
      if (step <= 0) step = computeStep(track);
      if (track.scrollWidth <= track.clientWidth) return; // nothing to scroll
      track.scrollTo({ left: index * step, behavior: smooth ? 'smooth' : 'auto' });
    }

    function onPrev() {
      if (!enabled() || !isMobile()) return;
      if (index <= 0) snap(maxStartIdx(), false);
      else snap(index - 1);
    }

    function onNext() {
      if (!enabled() || !isMobile()) return;
      if (index >= maxStartIdx()) snap(0, false);
      else snap(index + 1);
    }

    function onResize() {
      trackEl = getTrack();
      if (!trackEl) return;
      step = computeStep(trackEl);
      // keep the same visual items in view
      if (enabled() && isMobile()) snap(index, false);
      updateControls();
    }

    function onTrackScroll() {
      if (!enabled() || !isMobile() || !trackEl) return;
      if (step <= 0) step = computeStep(trackEl);
      if (rafScroll) cancelAnimationFrame(rafScroll);
      rafScroll = requestAnimationFrame(() => {
        const newIndex = Math.round(trackEl.scrollLeft / (step || 1));
        index = Math.min(Math.max(newIndex, 0), maxStartIdx());
      });
    }

    function delegateClicks(e) {
      const prev = e.target.closest('[data-mc-prev]');
      const next = e.target.closest('[data-mc-next]');
      if (prev) { e.preventDefault(); onPrev(); }
      else if (next) { e.preventDefault(); onNext(); }
    }

    function bindTrackScroll() {
      const nextTrack = getTrack();
      if (trackEl === nextTrack) return;
      if (trackEl) trackEl.removeEventListener('scroll', onTrackScroll, { passive: true });
      trackEl = nextTrack;
      if (trackEl) trackEl.addEventListener('scroll', onTrackScroll, { passive: true });
    }

    function updateControls() {
      const prevBtn = getPrev();
      const nextBtn = getNext();
      const itemsCount = getItems().length;
      const show = enabled() && isMobile() && itemsCount > 2 && trackEl && (trackEl.scrollWidth > trackEl.clientWidth + 1);
      if (prevBtn) prevBtn.style.display = show ? '' : 'none';
      if (nextBtn) nextBtn.style.display = show ? '' : 'none';
    }

    // Events
    root.addEventListener('click', delegateClicks);
    window.addEventListener('resize', onResize);

    const mo = new MutationObserver(() => {
      step = 0;
      bindTrackScroll();
      updateControls();
    });
    mo.observe(root, { childList: true, subtree: true });

    root.addEventListener('shopify:section:unload', () => {
      root.removeEventListener('click', delegateClicks);
      window.removeEventListener('resize', onResize);
      if (trackEl) trackEl.removeEventListener('scroll', onTrackScroll, { passive: true });
      mo.disconnect();
    });

    // Init
    bindTrackScroll();
    onResize();
  }

  function initAll() {
    document.querySelectorAll('[data-mc-root]').forEach(initCarousel);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
})();
